import I18n from '@/i18n';
import type { AiPhotoUploadResponse, UploadFailure } from '@/types/Upload';
import { AiPhotoUploadAbortedError, AiPhotoUploadError, photoRejectionKey, uploadAiPhoto } from '@/utils/aiPhotoUpload';
import type { Ref } from 'vue';
import { ref } from 'vue';

/** The transfer owns 0-90%; the tail eases toward 99% while the server stores and validates. */
const transferCeiling = 90;
const processingCeiling = 99;
const tailTickMs = 100;
const tailHalfLifeBaseMs = 3000;
const tailHalfLifePerMegabyteMs = 250;
const bytesPerMegabyte = 1024 * 1024;

export type AiPhotoUpload = {
    isUploading: Ref<boolean>;
    uploadProgress: Ref<number>;
    uploadFile: (file: File, endpoint: string, groupIdentifier?: string) => Promise<AiPhotoUploadResponse | null>;
    cancelUpload: () => void;
    onUploadError: (handler: (failure: UploadFailure) => void) => void;
};

function tailHalfLifeMs(fileSizeBytes: number): number {
    return tailHalfLifeBaseMs + (fileSizeBytes / bytesPerMegabyte) * tailHalfLifePerMegabyteMs;
}

function genericErrorMessage(): string {
    return I18n.global.t('pages.product_detail_page.product_info.product_button.upload.errors.generic');
}

// The transfer and its wire format live in utils/aiPhotoUpload; this class owns the progress UI state only.
class AiPhotoUploader {
    public readonly isUploading = ref<boolean>(false);
    public readonly uploadProgress = ref<number>(0);
    private errorHandler: ((failure: UploadFailure) => void) | null = null;
    private abortController: AbortController | undefined = undefined;
    private tailTicker: ReturnType<typeof setInterval> | undefined = undefined;

    public async uploadFile(file: File, endpoint: string, groupIdentifier?: string): Promise<AiPhotoUploadResponse | null> {
        const rejectionKey = photoRejectionKey(file);

        if (rejectionKey) {
            this.emitFailure({ message: I18n.global.t(rejectionKey) });

            return null;
        }

        this.isUploading.value = true;
        this.uploadProgress.value = 0;
        this.abortController = new AbortController();

        try {
            const response = await uploadAiPhoto(
                file,
                endpoint,
                groupIdentifier,
                (percentage: number) => this.reportTransferProgress(percentage),
                () => this.startTailProgress(file.size),
                this.abortController.signal,
            );

            this.uploadProgress.value = 100;

            return response;
        } catch (error: unknown) {
            if (error instanceof AiPhotoUploadAbortedError) {
                return null;
            }

            const failure = error instanceof AiPhotoUploadError ? error.failure : { message: genericErrorMessage() };

            // A response with a real HTTP status keeps its tail progress on screen; only a dropped connection clears it.
            if (failure.status === undefined) {
                this.uploadProgress.value = 0;
            }

            this.emitFailure(failure);

            return null;
        } finally {
            this.stopTailProgress();
            this.isUploading.value = false;
        }
    }

    public cancelUpload(): void {
        this.abortController?.abort();
        this.stopTailProgress();
        this.isUploading.value = false;
        this.uploadProgress.value = 0;
    }

    public onUploadError(handler: (failure: UploadFailure) => void): void {
        this.errorHandler = handler;
    }

    private reportTransferProgress(percentage: number): void {
        this.uploadProgress.value = Math.floor((percentage * transferCeiling) / 100);
    }

    // Nothing left to measure after the bytes are sent; each half-life halves the remaining gap toward the ceiling.
    private startTailProgress(fileSizeBytes: number): void {
        const startedAt = Date.now();
        const halfLifeMs = tailHalfLifeMs(fileSizeBytes);

        this.stopTailProgress();
        this.tailTicker = setInterval(() => this.reportTailProgress(Date.now() - startedAt, halfLifeMs), tailTickMs);
    }

    private stopTailProgress(): void {
        if (this.tailTicker === undefined) {
            return;
        }

        clearInterval(this.tailTicker);
        this.tailTicker = undefined;
    }

    private reportTailProgress(elapsedMs: number, halfLifeMs: number): void {
        if (!this.isUploading.value) {
            return;
        }

        const share = 1 - Math.pow(2, -elapsedMs / halfLifeMs);
        const tailSpan = processingCeiling - transferCeiling;

        this.uploadProgress.value = Math.max(this.uploadProgress.value, Math.floor(transferCeiling + share * tailSpan));
    }

    private emitFailure(failure: UploadFailure): void {
        if (this.errorHandler) {
            this.errorHandler(failure);
        }
    }
}

export function useAiPhotoUpload(): AiPhotoUpload {
    const uploader = new AiPhotoUploader();

    return {
        isUploading: uploader.isUploading,
        uploadProgress: uploader.uploadProgress,
        uploadFile: (file: File, endpoint: string, groupIdentifier?: string) => uploader.uploadFile(file, endpoint, groupIdentifier),
        cancelUpload: () => uploader.cancelUpload(),
        onUploadError: (handler: (failure: UploadFailure) => void) => uploader.onUploadError(handler),
    };
}
